/*
  Shiki is nice and simple inside Server Components, but what if the code snippet is interactive?

  Essentially what we want to do is render two things:
  1. The server-rendered snippet.
  2. A client-only snippet that gets swapped in after hydration.

  The tricky thing is that we can't do the swap right after hydration. Shiki’s `highlighter` is asynchronous, it takes some time to load. And even then, if we swap RIGHT after it loads, we'll see a flicker as the UI disappears while the highlighter works.

  So, we create a CSS grid with a single cell, and we assign both the server snippet and the client snippet to this cell. The client snippet receives an `onMount` callback, which it calls after the client snippet is genuinely ready to be displayed (see `ClientOnlySnippet` for info about that).

  We lazy-load ClientOnlySnippet to avoid doing unnecessary work during the SSR, and to speed up Time To Interactive.
*/
'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import dynamic from 'next/dynamic';

import { ColorMode } from '@/constants';

import type {
  SingleAnnotation,
  MultipleAnnotations,
} from '@/components/CodeSnippet/CodeSnippet.types';

const LazyClientOnlySnippet = dynamic(
  () => import('./ClientOnlySnippet'),
  { ssr: false }
);

function SnippetJuggler({
  serverSnippet,
  ...delegated
}: {
  serverSnippet: React.ReactNode;
  lockedToColorMode?: ColorMode | false;
  includeContentStyles?: boolean;
  omitCopyButton?: boolean;
  highlight?: SingleAnnotation | MultipleAnnotations;
}) {
  const [mounted, setMounted] = React.useState(false);

  return (
    <Grid>
      {mounted ? null : <Child>{serverSnippet}</Child>}
      <Child
        style={{
          opacity: mounted ? 1 : 0,
          pointerEvents: mounted ? 'auto' : 'none',
        }}
      >
        <LazyClientOnlySnippet
          {...delegated}
          onMount={() => setMounted(true)}
        />
      </Child>
    </Grid>
  );
}

const Grid = styled.div`
  display: grid;
`;

const Child = styled.div`
  grid-area: 1 / 1;
`;

export default SnippetJuggler;
